import React from "react"
import PropTypes from "prop-types"

import { federalRegions } from "shared/imports/regionConstants"

import { MAX_BILL_SEARCHIFY_SELECTIONS } from "shared/search/constants"

import {
    datePickerGenericFilter,
    enumLabelSort,
    enumOrderSort,
    enumSingleGenericFilter,
    enumValueSort,
    createProfileToggleFilter,
    textGenericFilter,
} from "shared/search/containers/Filter/Filters"
import {
    enumGenericFilter,
    searchifySelectGenericFilter,
    toggleGenericFilter,
} from "shared/search/containers/Filter/FilterCreators"

import {
    AmendmentFilterableListView,
    BillFilterableListView,
    OfficialFilterableListView,
    VoteFilterableListView,
} from "shared/search/containers/FilterableListView/FilterableListViews"

import {
    AmendmentSelect,
    BillSelect,
    OfficialSelect,
    VoteSelect,
} from "app/static/frontend/selects/containers/PieSelect"
import { DateListNoFutureSelect } from "app/static/frontend/selects/containers/EnumSelect"

const defaultPropTypes = {
    displayLabel: PropTypes.string,
    filterComponent: PropTypes.func,
    filterKey: PropTypes.string,
    uniqueFilterId: PropTypes.string,

    dataTypeLabel: PropTypes.string,
    helpText: PropTypes.string,
    selectComponent: PropTypes.func,
    regions: PropTypes.array,
    placeholderText: PropTypes.string,
}

const defaultEnumPropTypes = {
    displayLabel: PropTypes.string,
    filterComponent: PropTypes.func,
    filterKey: PropTypes.string,
    uniqueFilterId: PropTypes.string,

    enumItems: PropTypes.array,
    helpText: PropTypes.string,
    sortBy: PropTypes.func,
}

const dateDefaultPropTypes = {
    displayLabel: PropTypes.string,
    filterComponent: PropTypes.func,
    filterKey: PropTypes.string,
    uniqueFilterId: PropTypes.string,

    noFuture: PropTypes.bool,
}

/** Result Type */
export const VoteResultTypeFilter = (props) => {
    const FilterComponent = props.filterComponent || enumGenericFilter
    return <FilterComponent {...props} />
}
VoteResultTypeFilter.propTypes = {
    filterComponent: PropTypes.func,
    uniqueFilterId: PropTypes.string,
    filterKey: PropTypes.string,
    enumItems: PropTypes.array,
    sortBy: PropTypes.func,
    displayLabel: PropTypes.string,
}
VoteResultTypeFilter.defaultProps = {
    filterComponent: enumGenericFilter,
    uniqueFilterId: "voteResultTypeFilter",
    filterKey: "vote_result_type__in",
    enumItems: DjangIO.app.vote.models.VoteResultType.region_supported_items(),
    sortBy: enumValueSort,
}

export const VoteResultPersonFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent searchifyFLV={OfficialFilterableListView} {...props} />
}
VoteResultPersonFilter.propTypes = {
    filterComponent: PropTypes.func,
    uniqueFilterId: PropTypes.string,
    filterKey: PropTypes.string,
    dataTypeLabel: PropTypes.string,
    selectComponent: PropTypes.func,
}
VoteResultPersonFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    uniqueFilterId: "voteResultPersonFilter",
    filterKey: "about_person",
    dataTypeLabel: "Officials",
    selectComponent: OfficialSelect,
}

export const VoteResultAmendmentFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent searchifyFLV={AmendmentFilterableListView} {...props} />
}
VoteResultAmendmentFilter.propTypes = {
    filterComponent: PropTypes.func,
    uniqueFilterId: PropTypes.string,
    filterKey: PropTypes.string,
    dataTypeLabel: PropTypes.string,
    selectComponent: PropTypes.func,
    regions: PropTypes.array,
}
VoteResultAmendmentFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    uniqueFilterId: "voteResultAmendmentFilter",
    filterKey: "about_amendment",
    dataTypeLabel: "Amendments",
    selectComponent: AmendmentSelect,
    regions: federalRegions,
}

export const VoteResultBillFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent searchifyFLV={BillFilterableListView} {...props} />
}
VoteResultBillFilter.propTypes = {
    filterComponent: PropTypes.func,
    uniqueFilterId: PropTypes.string,
    filterKey: PropTypes.string,
    dataTypeLabel: PropTypes.string,
    selectComponent: PropTypes.func,
    maxSearchifySelections: PropTypes.number,
}
VoteResultBillFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    uniqueFilterId: "voteResultBillFilter",
    filterKey: "about_bill",
    dataTypeLabel: "Bills",
    selectComponent: BillSelect,
    maxSearchifySelections: MAX_BILL_SEARCHIFY_SELECTIONS,
}

export const VoteResultVoteFilter = (props) => {
    const FilterComponent = props.filterComponent || searchifySelectGenericFilter
    return <FilterComponent searchifyFLV={VoteFilterableListView} {...props} />
}
VoteResultVoteFilter.propTypes = {
    filterComponent: PropTypes.func,
    uniqueFilterId: PropTypes.string,
    filterKey: PropTypes.string,
    dataTypeLabel: PropTypes.string,
    selectComponent: PropTypes.func,
}
VoteResultVoteFilter.defaultProps = {
    filterComponent: searchifySelectGenericFilter,
    uniqueFilterId: "voteResultVoteFilter",
    filterKey: "about_vote",
    dataTypeLabel: "Bills",
    selectComponent: VoteSelect,
}

export const VoteResultVotedWithOrAgainstFilter = createProfileToggleFilter({
    uniqueFilterId: "voteResultVotedWithOrAgainstFilter",
    trueText: "Voted With Party",
    falseText: "Voted Against Party",
    filterKeyMapping: {
        true: "voted_with_party",
        false: "voted_against_party",
    },
})

VoteResultVotedWithOrAgainstFilter.propTypes = {
    uniqueFilterId: PropTypes.string,
    trueText: PropTypes.string,
    falseText: PropTypes.string,
    filterKeyMapping: PropTypes.objectOf(PropTypes.string),
}

export const VoteResultPartyFilter = (props) => {
    const FilterComponent = props.filterComponent || enumGenericFilter
    return <FilterComponent {...props} />
}
VoteResultPartyFilter.propTypes = {
    filterComponent: PropTypes.func,
    uniqueFilterId: PropTypes.string,
    filterKey: PropTypes.string,
    enumItems: PropTypes.array,
    sortBy: PropTypes.func,
}
VoteResultPartyFilter.defaultProps = {
    filterComponent: enumGenericFilter,
    uniqueFilterId: "voteResultPartyFilter",
    filterKey: "person_most_recent_party__in",
    enumItems: DjangIO.app.person.types.Party.region_supported_items().filter(
        (item) => item.value !== DjangIO.app.person.types.Party.NoParty.value,
    ),
    sortBy: enumLabelSort,
}

/** Title */
export const VoteResultBillTitleFilter = (props) => {
    const FilterComponent = props.filterComponent || textGenericFilter
    return <FilterComponent {...props} />
}
VoteResultBillTitleFilter.propTypes = defaultPropTypes
VoteResultBillTitleFilter.defaultProps = {
    filterComponent: textGenericFilter,
    uniqueFilterId: "voteResultBillTitleFilter",
    filterKey: "bill_title_search",
    placeholderText: "Search text of Bill title...",
    displayLabel: "Search Titles",
}

export const VoteResultAmendmentTitlteFilter = (props) => {
    const FilterComponent = props.filterComponent || textGenericFilter
    return <FilterComponent {...props} />
}
VoteResultAmendmentTitlteFilter.propTypes = defaultPropTypes
VoteResultAmendmentTitlteFilter.defaultProps = {
    filterComponent: textGenericFilter,
    uniqueFilterId: "voteResultAmendmentTitlteFilter",
    filterKey: "amendment_title_search",
    placeholderText: "Search text of Amendment title...",
}

/** Description */
export const VoteResultQuestionFilter = (props) => {
    const FilterComponent = props.filterComponent || textGenericFilter
    return <FilterComponent {...props} />
}
VoteResultQuestionFilter.propTypes = defaultPropTypes
VoteResultQuestionFilter.defaultProps = {
    filterComponent: textGenericFilter,
    uniqueFilterId: "voteResultQuestionFilter",
    filterKey: "question_search",
    placeholderText: "Search text of Vote question...",
    displayLabel: "Search Questions",
}

/** Date */
export const VoteResultDateListNoFutureFilter = (props) => {
    const FilterComponent = props.filterComponent || enumSingleGenericFilter
    return <FilterComponent {...props} />
}
VoteResultDateListNoFutureFilter.propTypes = defaultEnumPropTypes
VoteResultDateListNoFutureFilter.defaultProps = {
    filterComponent: enumSingleGenericFilter,
    uniqueFilterId: "voteResultDateListNoFutureFilter",
    filterKey: "date_range_created",
    enumItems: DjangIO.app.events.models.DateRangeType.region_supported_items().filter(
        (item) =>
            ![
                DjangIO.app.events.models.DateRangeType.future.value,
                DjangIO.app.events.models.DateRangeType.next_month.value,
                DjangIO.app.events.models.DateRangeType.next_week.value,
            ].includes(item.value),
    ),
    sortBy: enumOrderSort,
    displayLabel: "Date Vote was Cast",
    helpText: "Filter for votes before/after a particular date " + "or within a specific date range.",
    selectComponent: DateListNoFutureSelect,
}

export const VoteResultDatePickerStartNoFutureFilter = (props) => {
    const FilterComponent = props.filterComponent || datePickerGenericFilter
    return <FilterComponent {...props} />
}
VoteResultDatePickerStartNoFutureFilter.propTypes = dateDefaultPropTypes
VoteResultDatePickerStartNoFutureFilter.defaultProps = {
    filterComponent: datePickerGenericFilter,
    uniqueFilterId: "voteResultDatePickerStartNoFutureFilter",
    filterKey: "vote_created__gt",
    displayLabel: "Start Date",
    noFuture: true,
}

export const VoteResultDatePickerEndNoFutureFilter = (props) => {
    const FilterComponent = props.filterComponent || datePickerGenericFilter
    return <FilterComponent {...props} />
}
VoteResultDatePickerEndNoFutureFilter.propTypes = dateDefaultPropTypes
VoteResultDatePickerEndNoFutureFilter.defaultProps = {
    filterComponent: datePickerGenericFilter,
    uniqueFilterId: "voteResultDatePickerEndNoFutureFilter",
    filterKey: "vote_created__lt",
    displayLabel: "End Date",
    noFuture: true,
}

export const VoteResultBillExistsFilter = (props) => {
    const FilterComponent = props.filterComponent || toggleGenericFilter
    return <FilterComponent {...props} />
}
VoteResultBillExistsFilter.propTypes = {
    filterComponent: PropTypes.func,
    uniqueFilterId: PropTypes.string,
    filterKey: PropTypes.string,
    displayLabel: PropTypes.string,
    trueText: PropTypes.string,
    falseText: PropTypes.string,
}
VoteResultBillExistsFilter.defaultProps = {
    filterComponent: toggleGenericFilter,
    uniqueFilterId: "VoteResultBillExistsFilter",
    filterKey: "bill_exists",
    displayLabel: "Voted on Bill",
    trueText: "Yes",
    falseText: "No",
}

export const VoteResultAmendmentExistsFilter = (props) => {
    const FilterComponent = props.filterComponent || toggleGenericFilter
    return <FilterComponent {...props} />
}
VoteResultAmendmentExistsFilter.propTypes = {
    filterComponent: PropTypes.func,
    uniqueFilterId: PropTypes.string,
    filterKey: PropTypes.string,
    displayLabel: PropTypes.string,
    trueText: PropTypes.string,
    falseText: PropTypes.string,
}
VoteResultAmendmentExistsFilter.defaultProps = {
    filterComponent: toggleGenericFilter,
    uniqueFilterId: "voteResultAmendmentExistsFilter",
    filterKey: "amendment_exists",
    displayLabel: "Voted on Amendment",
    trueText: "Yes",
    falseText: "No",
}
